/**
 * The doctype "i": what a writer can expect before picking a document type —
 * the guidance drafting follows, what the drafting agent can do, and the
 * sources it usually draws on. Read-only; the words come from the server
 * (``GET /doc-types/{code}/about``), which builds them from what a run uses.
 *
 * SRP: presentation only. The picker owns loading and caching.
 */
import type React from "react";
import type { DoctypeAbout as DoctypeAboutData } from "../../api/client";

export type DoctypeAboutState = DoctypeAboutData | "loading" | "error";

export const DoctypeAbout: React.FC<{ about: DoctypeAboutState }> = ({ about }) => {
  if (about === "loading") {
    return (
      <div className="as-doctype-about" data-testid="doctype-about">
        <p className="as-note">Loading…</p>
      </div>
    );
  }
  if (about === "error") {
    return (
      <div className="as-doctype-about" data-testid="doctype-about">
        <p className="as-note">Couldn’t load the details for this document type.</p>
      </div>
    );
  }
  const { primary, secondary } = about.guidance;
  return (
    <div className="as-doctype-about" data-testid="doctype-about">
      {primary.length > 0 ? (
        <AboutBlock title="Follows">
          {primary.map((g) => (
            <li key={g}>{g}</li>
          ))}
          {secondary.map((g) => (
            <li key={g} className="as-doctype-about-secondary">
              {g} <span>(also)</span>
            </li>
          ))}
        </AboutBlock>
      ) : null}
      {about.tools.length > 0 ? (
        <AboutBlock title="While drafting, it">
          {about.tools.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </AboutBlock>
      ) : null}
      {about.sources.length > 0 ? (
        <AboutBlock title="Usually drafts from">
          {about.sources.map((s) => (
            <li key={s.name}>
              {s.name}
              {s.core ? <span className="as-doctype-about-core"> · core</span> : null}
            </li>
          ))}
        </AboutBlock>
      ) : null}
    </div>
  );
};

const AboutBlock: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="as-doctype-about-block">
    <div className="as-doctype-about-title">{title}</div>
    <ul>{children}</ul>
  </div>
);

export default DoctypeAbout;
